import CONFIG_QUERY from '@Queries/config_queries.graphql';
import RESET_PASSWORD_QUERY from '@Queries/reset_password_token_validator.query.graphql';
import { getUrlSearchParameters } from '@Utils/urls';
import INVITE_QUERY from '@Queries/invite_query.graphql';
import VALIDATE_AUTHENTICATION_QUERY from '@Queries/validate_auth_query.graphql';
import { findToken, storeCookieFromToken } from './tokenHelper';
import { LANGUAGE } from './language';

export async function fetchAppOnlyConfig(apolloClient) {
  try {
    const configQueryResponse = await apolloClient.query({
      query: CONFIG_QUERY,
      fetchPolicy: 'no-cache',
    });

    return { ...configQueryResponse.data, language: LANGUAGE };
  } catch (error) {
    //User is not logged in
  }
}

async function fetchConfigFromInvite(apolloClient, inviteId, setLoggedInUser, preAuthConfig) {
  try {
    const inviteQueryResponse = await apolloClient.query({
      query: INVITE_QUERY,
      variables: { inviteId },
      fetchPolicy: 'no-cache',
    });
    const invitedUser = inviteQueryResponse.data.invitedUser;
    // if the user is already logged in, the user ID will be missing
    // for reasons that are not clear!!
    const isAlreadyLoggedIn = !invitedUser.id;
    if (isAlreadyLoggedIn) {
      return validateAuthentication(apolloClient, false, setLoggedInUser, preAuthConfig);
    }
    setLoggedInUser(invitedUser);
    return fetchAppOnlyConfig(apolloClient);
  } catch (error) {
    // User is not logged in
  }
}

const storeCombinedAccessCookie = () => {
  const token = findToken();
  if (!token || !token.auth_token) {
    return;
  }
  storeCookieFromToken(token);
};

async function validateToken(apolloClient, tryWithLegacyCookieOnError, setLoggedInUser) {
  try {
    const response = await apolloClient.query({
      query: VALIDATE_AUTHENTICATION_QUERY,
      fetchPolicy: 'no-cache',
    });

    return response;
  } catch (error) {
    // this will try with legacy angular auth-headers cookie to login to app
    if (tryWithLegacyCookieOnError) {
      storeCombinedAccessCookie();
      return validateToken(apolloClient, false, setLoggedInUser);
    }
    // User is not logged in
  }
}

async function validateAuthentication(apolloClient, tryWithLegacyCookieOnError, setLoggedInUser, preAuthConfig) {
  try {
    if (preAuthConfig.idsAuthenticationFeatureFlag) {
      const userResponse = await fetch(`${window.baseAppConfig.bffPath}/user`);
      if (userResponse.ok) {
        const [tokenResponse] = await Promise.all([
          validateToken(apolloClient, tryWithLegacyCookieOnError, setLoggedInUser),
        ]);
        setLoggedInUser(tokenResponse.data.validateToken);
        return fetchAppOnlyConfig(apolloClient);
      }
    } else {
      const [tokenResponse] = await Promise.all([
        validateToken(apolloClient, tryWithLegacyCookieOnError, setLoggedInUser),
      ]);
      setLoggedInUser(tokenResponse.data.validateToken);
      return fetchAppOnlyConfig(apolloClient);
    }
  } catch (error) {
    // User is not logged in
  }
}

async function fetchConfigWithResetPasswordToken(apolloClient, reset_password_token, companyId, preAuthConfig) {
  try {
    const inviteQueryResponse = await apolloClient.query({
      query: RESET_PASSWORD_QUERY,
      variables: { resetPasswordToken: reset_password_token, cid: companyId },
      fetchPolicy: 'no-cache',
    });
    if (inviteQueryResponse) {
      return validateAuthentication(apolloClient, true, null, preAuthConfig);
    }
  } catch (error) {
    // User is not logged in
  }
}

export default async function fetchConfig(apolloClient, currentUrlString, setLoggedInUser, preAuthConfig) {
  const { inviteId, reset_password_token, cid } = getUrlSearchParameters(currentUrlString);

  if (reset_password_token) {
    await fetchConfigWithResetPasswordToken(apolloClient, reset_password_token, cid, preAuthConfig);
  }
  if (inviteId) {
    return fetchConfigFromInvite(apolloClient, inviteId, setLoggedInUser, preAuthConfig);
  }

  //default path if not resetting password or provided inviteId
  return validateAuthentication(apolloClient, true, setLoggedInUser, preAuthConfig);
}
